import { useMemo, useState } from 'react'
import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { AppIcon } from '../ui/icon'
import { mdiOpenInNew } from '@mdi/js'
import { useLocale } from '../../contexts/LocaleContext'
import { useFormatters } from '../../lib/format'
import { useCan } from '../../contexts/PermissionsContext'
import { Badge } from '../ui/badge'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Skeleton } from '../ui/skeleton'
import { Alert, AlertDescription } from '../ui/alert'
import {
    useGroupMatrix,
    useGroupSummary,
    useGroupMutations,
    useKPIRegistry,
    useKPITargets,
    type KPIDefinition,
    type KPITargetInput,
    type Level,
    type Period,
} from '../../api/groupSteering'
import { formatUnit, langOf, text } from './format'
import { TierBadge } from './primitives'
import { PageHeader } from '../shared/PageHeader'
import { SECTION_ICONS } from '../../lib/section-icons'
import { EmptyState } from '../ui/empty-state'

/**
 * The two resource pages, generated from the registry and the targets —
 * never written by hand — plus the perimeters & access page.
 */

function effectiveOf(targets: ReturnType<typeof useKPITargets>['data'], code: string) {
    return targets?.effective?.[code]
}

export function DefinitionsView({ org }: { org: string }) {
    const { t } = useTranslation('group-steering')
    const { locale } = useLocale()
    const lang = langOf(locale)
    const { formatDate } = useFormatters()
    const registry = useKPIRegistry(org)
    const canConfig = useCan('configuration.read')
    const targets = useKPITargets(org, canConfig)
    if (registry.isLoading) return <Skeleton className="h-64 w-full" />
    const families = registry.data?.families ?? []
    const kpis = registry.data?.kpis ?? []
    return (
        <div className="space-y-6" data-testid="group-definitions">
            <PageHeader
                icon={SECTION_ICONS.types}
                title={t('definitions.title')}
                description={t('definitions.intro')}
                data-testid="group-definitions-title"
            />
            {families.map((fam) => {
                const list = kpis.filter((k) => k.family === fam.code)
                if (list.length === 0) return null
                return (
                    <section key={fam.code} className="space-y-2" data-testid={`definitions-family-${fam.code}`}>
                        <h2 className="text-lg font-medium text-foreground">{text(fam.label, lang)}</h2>
                        <p className="text-sm text-muted-foreground">{text(fam.question, lang)}</p>
                        <div className="space-y-2">
                            {list.map((k) => (
                                <DefinitionEntry key={k.code} k={k} lang={lang} locale={locale} effective={effectiveOf(targets.data, k.code)} formatDate={formatDate} />
                            ))}
                        </div>
                    </section>
                )
            })}
        </div>
    )
}

function DefinitionEntry({ k, lang, locale, effective, formatDate }: { k: KPIDefinition; lang: 'en' | 'fr'; locale: string; effective: { target?: number; valid_from?: string; source: string } | undefined; formatDate: (d: string, o?: Intl.DateTimeFormatOptions) => string }) {
    const { t } = useTranslation('group-steering')
    return (
        <article className={`rounded-lg border bg-card p-3 ${k.availability === 'planned' ? 'opacity-70' : ''}`} data-testid={`definition-${k.code}`}>
            <div className="flex flex-wrap items-baseline gap-2">
                <span className="font-medium text-foreground">
                    {k.pilot ? '★ ' : ''}
                    {text(k.label, lang)}
                </span>
                <span className="font-mono text-[11px] text-muted-foreground">
                    {k.code} · v{k.version}
                </span>
                {k.availability === 'planned' && <Badge variant="outline" className="text-[10px]">{t('definitions.planned')}</Badge>}
            </div>
            <p className="text-sm text-muted-foreground mt-1">{text(k.definition, lang)}</p>
            <dl className="mt-2 grid grid-cols-2 md:grid-cols-5 gap-x-3 gap-y-1 text-xs">
                <div><dt className="text-muted-foreground">{t('definitions.unit')}</dt><dd className="text-foreground">{t(`definitions.unit_${k.unit}`)}</dd></div>
                <div><dt className="text-muted-foreground">{t('definitions.direction')}</dt><dd className="text-foreground">{t(`definitions.direction_${k.direction}`)}</dd></div>
                <div><dt className="text-muted-foreground">{t('definitions.attribution')}</dt><dd className="text-foreground">{t(`definitions.attribution_${k.attribution}`)}</dd></div>
                <div><dt className="text-muted-foreground">{t('definitions.denominator')}</dt><dd className="text-foreground">{text(k.denominator, lang)}</dd></div>
                <div><dt className="text-muted-foreground">{t('definitions.floor')}</dt><dd className="text-foreground">{t('definitions.floor_value', { count: k.min_n })}</dd></div>
            </dl>
            {(effective?.target !== undefined || k.default_target !== undefined) && (
                <p className="mt-2 text-xs">
                    <span className="text-muted-foreground">{t('definitions.target')}</span>{' '}
                    <span className="text-foreground font-medium">{formatUnit(effective?.target ?? k.default_target, k.unit, lang, locale)}</span>{' '}
                    <span className="text-muted-foreground">
                        {effective?.source === 'organization' && effective.valid_from ? t('definitions.target_since', { date: formatDate(effective.valid_from, { dateStyle: 'short' }) }) : t('definitions.target_default')}
                    </span>
                </p>
            )}
            {k.confusable && (
                <p className="mt-1 text-xs">
                    <span className="text-muted-foreground">{t('definitions.confusable')}</span> <span className="text-foreground">{text(k.confusable, lang)}</span>
                </p>
            )}
        </article>
    )
}

export function CriteriaView({ org, period, level }: { org: string; period: Period; level: Level }) {
    const { t } = useTranslation('group-steering')
    const { locale } = useLocale()
    const lang = langOf(locale)
    const { formatDate } = useFormatters()
    const registry = useKPIRegistry(org)
    const canWrite = useCan('configuration.write')
    const canRead = useCan('configuration.read')
    const targets = useKPITargets(org, canRead)
    const summary = useGroupSummary(org, period, level)
    const { setTargets } = useGroupMutations(org)
    const [editing, setEditing] = useState(false)
    const [draft, setDraft] = useState<Record<string, { target?: string; tier_excellent?: string; tier_watch?: string }>>({})
    const [saved, setSaved] = useState(false)
    const pilot = useMemo(() => (registry.data?.kpis ?? []).filter((k) => k.pilot), [registry.data])
    const watchCounts = useMemo(() => {
        const out: Record<string, number> = {}
        for (const f of summary.data?.families ?? []) for (const tl of f.tiles) out[tl.kpi.code] = tl.out_of_target.count
        return out
    }, [summary.data])
    if (registry.isLoading) return <Skeleton className="h-64 w-full" />
    const floors = registry.data?.floors
    const eff = (code: string) => effectiveOf(targets.data, code)
    const save = async () => {
        const entries: KPITargetInput[] = []
        for (const [code, v] of Object.entries(draft)) {
            for (const kind of ['target', 'tier_excellent', 'tier_watch'] as const) {
                const raw = v[kind]
                if (raw === undefined || raw === '') continue
                const k = pilot.find((p) => p.code === code)
                const num = Number(raw.replace(',', '.'))
                if (Number.isNaN(num)) continue
                entries.push({ kpi_code: code, kind, value: k?.unit === 'ratio' ? num / 100 : num })
            }
        }
        if (entries.length === 0) return
        await setTargets.mutateAsync(entries)
        setDraft({})
        setEditing(false)
        setSaved(true)
    }
    const inputOf = (k: KPIDefinition, kind: 'target' | 'tier_excellent' | 'tier_watch', current: number | undefined) => (
        <Input
            className="h-7 w-24 text-xs"
            value={draft[k.code]?.[kind] ?? (current === undefined ? '' : k.unit === 'ratio' ? String(Math.round(current * 100)) : String(current))}
            onChange={(e) => setDraft((d) => ({ ...d, [k.code]: { ...d[k.code], [kind]: e.target.value } }))}
            data-testid={`target-input-${k.code}-${kind}`}
        />
    )
    return (
        <div className="space-y-6" data-testid="group-criteria">
            <PageHeader
                icon={SECTION_ICONS.businessRules}
                title={t('criteria.title')}
                description={t('criteria.intro')}
                data-testid="group-criteria-title"
                actions={
                    <>
                        {canWrite && !editing && (
                            <Button variant="outline" onClick={() => { setEditing(true); setSaved(false) }} data-testid="criteria-edit-targets">
                                {t('criteria.edit_targets')}
                            </Button>
                        )}
                        {editing && (
                            <>
                                <Button variant="outline" onClick={() => { setEditing(false); setDraft({}) }}>{t('common:cancel', 'Cancel')}</Button>
                                <Button onClick={save} disabled={setTargets.isPending} data-testid="criteria-save-targets">{t('criteria.save')}</Button>
                            </>
                        )}
                    </>
                }
            />
            {saved && (
                <Alert data-testid="criteria-saved">
                    <AlertDescription>{t('criteria.saved')}</AlertDescription>
                </Alert>
            )}
            <section className="space-y-2" data-testid="criteria-tiers">
                <h2 className="text-lg font-medium text-foreground">{t('criteria.tiers_title')}</h2>
                <div className="overflow-x-auto rounded-lg border bg-card">
                    <table className="w-full text-sm">
                        <thead className="text-xs text-muted-foreground">
                            <tr className="text-left">
                                <th className="py-2 px-3 font-medium">{t('exceptions.indicator')}</th>
                                <th className="py-2 px-3 font-medium">{t('criteria.target_value')}</th>
                                <th className="py-2 px-3 font-medium">{t('criteria.tier_excellent')}</th>
                                <th className="py-2 px-3 font-medium">{t('tier.healthy')}</th>
                                <th className="py-2 px-3 font-medium">{t('criteria.tier_watch')}</th>
                                <th className="py-2 px-3 font-medium text-right">{t('criteria.watch_count_header')}</th>
                                <th className="py-2 px-3 font-medium">{t('criteria.source_header')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {pilot.map((k) => {
                                const e = eff(k.code)
                                const target = e?.target ?? k.default_target
                                const ex = e?.tier_excellent ?? k.default_tier_excellent
                                const wa = e?.tier_watch ?? k.default_tier_watch
                                const f = (v: number | undefined) => formatUnit(v, k.unit, lang, locale)
                                const better = k.direction === 'higher_better'
                                return (
                                    <tr key={k.code} className="border-t" data-testid={`criteria-row-${k.code}`}>
                                        <td className="py-2 px-3">
                                            <div className="font-medium text-foreground">★ {text(k.label, lang)}</div>
                                            <div className="font-mono text-[11px] text-muted-foreground">{k.code}</div>
                                        </td>
                                        <td className="py-2 px-3 tabular-nums">{editing ? inputOf(k, 'target', target) : f(target)}</td>
                                        <td className="py-2 px-3 tabular-nums">
                                            {editing ? inputOf(k, 'tier_excellent', ex) : <TierBadge tier="excellent" bound={ex === undefined ? '' : `${better ? '≥' : '≤'} ${f(ex)}`} testId={`criteria-${k.code}-excellent`} />}
                                        </td>
                                        <td className="py-2 px-3 tabular-nums">
                                            {ex !== undefined && wa !== undefined ? <TierBadge tier="healthy" bound={better ? `${f(wa)} – ${f(ex)}` : `${f(ex)} – ${f(wa)}`} testId={`criteria-${k.code}-healthy`} /> : '—'}
                                        </td>
                                        <td className="py-2 px-3 tabular-nums">
                                            {editing ? inputOf(k, 'tier_watch', wa) : <TierBadge tier="watch" bound={wa === undefined ? '' : `${better ? '<' : '>'} ${f(wa)}`} testId={`criteria-${k.code}-watch`} />}
                                        </td>
                                        <td className="py-2 px-3 text-right tabular-nums">{watchCounts[k.code] ?? '—'}</td>
                                        <td className="py-2 px-3 text-[11px] text-muted-foreground">
                                            {e?.source === 'organization' && e.valid_from ? t('criteria.since', { date: formatDate(e.valid_from, { dateStyle: 'short' }) }) : t('definitions.target_default')}
                                        </td>
                                    </tr>
                                )
                            })}
                        </tbody>
                    </table>
                </div>
            </section>
            <section className="space-y-1" data-testid="criteria-floors">
                <h2 className="text-lg font-medium text-foreground">{t('criteria.floors_title')}</h2>
                <ul className="text-sm text-muted-foreground list-disc pl-5">
                    <li>{t('criteria.floor_cohort', { count: floors?.min_cohort ?? 5 })}</li>
                    <li>{t('criteria.floor_nps', { count: floors?.min_nps_responses ?? 30 })}</li>
                    <li>{t('criteria.floor_peers', { count: floors?.min_peers ?? 5 })}</li>
                </ul>
            </section>
            <section className="space-y-1" data-testid="criteria-limits">
                <h2 className="text-lg font-medium text-foreground">{t('criteria.limits_title')}</h2>
                <ol className="text-sm text-muted-foreground list-decimal pl-5 space-y-0.5">
                    {[1, 2, 3, 4, 5].map((i) => (
                        <li key={i}>{t(`criteria.limit_${i}`)}</li>
                    ))}
                </ol>
            </section>
            <section className="space-y-1" data-testid="criteria-manager">
                <h2 className="text-lg font-medium text-foreground">{t('criteria.manager_comparison_title')}</h2>
                <p className="text-sm text-muted-foreground">{summary.data?.manager_comparison_enabled ? t('criteria.manager_comparison_on') : t('criteria.manager_comparison_off')}</p>
            </section>
            <section className="space-y-1" data-testid="criteria-policy">
                <h2 className="text-lg font-medium text-foreground">{t('criteria.policy_title')}</h2>
                <p className="text-sm text-muted-foreground">{t('criteria.policy_body')}</p>
            </section>
        </div>
    )
}

export function AccessView({ org, period, level }: { org: string; period: Period; level: Level }) {
    const { t } = useTranslation('group-steering')
    const entities = useGroupMatrix(org, period, 'entity')
    const agencies = useGroupMatrix(org, period, 'agency')
    const portfolios = useGroupMatrix(org, period, 'portfolio')
    void level
    const loading = entities.isLoading || agencies.isLoading
    if (loading) return <Skeleton className="h-64 w-full" />
    const named = (rows: { perimeter: { uid?: string; name?: string; anonymous?: boolean; parent_uid?: string; kind: string }; context?: { units: number; buildings: number; sites: number } }[] | undefined) => (rows ?? []).filter((r) => !r.perimeter.anonymous)
    const ents = named(entities.data?.rows)
    const ags = named(agencies.data?.rows)
    const pfs = named(portfolios.data?.rows)
    const row = (r: (typeof ents)[number], indent: number) => (
        <li key={r.perimeter.uid} className="flex items-center justify-between gap-2 py-1.5 border-b last:border-b-0" style={{ paddingLeft: indent * 16 }} data-testid={`access-node-${r.perimeter.uid}`}>
            <div className="min-w-0">
                <span className="text-sm text-foreground">{r.perimeter.name}</span>
                <span className="ml-2 text-[11px] text-muted-foreground">{t(`level.${r.perimeter.kind}`)}</span>
            </div>
            <div className="flex items-center gap-3 text-xs text-muted-foreground">
                {r.context && <span>{t('perimeters.context', { units: r.context.units, buildings: r.context.buildings, sites: r.context.sites })}</span>}
                {r.perimeter.uid && (
                    <Link to="/$org/assets/$assetUid" params={{ org, assetUid: r.perimeter.uid }} className="inline-flex items-center gap-1 hover:text-foreground" title={t('access.open_asset')} aria-label={t('access.open_asset')}>
                        <AppIcon path={mdiOpenInNew} size="xs" />
                    </Link>
                )}
            </div>
        </li>
    )
    const orphans = ags.filter((a) => !a.perimeter.parent_uid || !ents.some((e) => e.perimeter.uid === a.perimeter.parent_uid))
    return (
        <div className="space-y-4" data-testid="group-access">
            <PageHeader
                icon={SECTION_ICONS.accessGrants}
                title={t('access.title')}
                description={t('access.intro')}
                data-testid="group-access-title"
            />
            {ents.length === 0 && ags.length === 0 ? (
                <EmptyState title={t('access.empty')} data-testid="access-empty" />
            ) : (
                <ul className="rounded-lg border bg-card px-3">
                    {ents.map((e) => [
                        row(e, 0),
                        ...ags.filter((a) => a.perimeter.parent_uid === e.perimeter.uid).map((a) => [row(a, 1), ...pfs.filter((p) => p.perimeter.parent_uid === a.perimeter.uid).map((p) => row(p, 2))]),
                    ])}
                    {orphans.map((a) => [row(a, 0), ...pfs.filter((p) => p.perimeter.parent_uid === a.perimeter.uid).map((p) => row(p, 1))])}
                </ul>
            )}
        </div>
    )
}
